import { useRef } from 'react';
import { onClickPageButtonTracking, usePageTracking, useScrollTracking } from '../../../hooks/pageTracking';
import { LandingButtonClickActionType, UserActionEnum } from '../../../types/customLogger';
import { userAnalytics } from '../../../utils/analytics/userAnalytics';
import { SLEEP_EXPERT_CARDS as EXPERT_CARDS } from './constants';

const PAGE_NAME = 'lp_sleep_ai_recommendation';
const CTA_BUTTON_LABEL = '나에게 맞는 수면 사운드 들으러 가기';
const AI_DEEP_LINK = 'https://limh6.app.link/QULGgq9UX2b';

export default function LpAiRecommendation() {
  const scrollRef = useRef<HTMLDivElement>(null);

  usePageTracking(PAGE_NAME);
  useScrollTracking(scrollRef, PAGE_NAME);

  const onClickAiCta = () => {
    onClickPageButtonTracking(PAGE_NAME, CTA_BUTTON_LABEL);
    const action = `${UserActionEnum.LANDING_BUTTON_CLICK}_${CTA_BUTTON_LABEL}` as LandingButtonClickActionType;
    userAnalytics(action, PAGE_NAME);
    window.location.href = AI_DEEP_LINK;
  };

  return (
    <div ref={scrollRef} className="h-[100dvh] overflow-y-auto bg-[#101010]">
      <div className="mx-auto w-full max-w-[1200px]">
        <section className="px-4 pb-12 pt-12 text-center md:px-8 md:pt-14">
          <p className="text-[clamp(24px,3.8vw,50px)] font-medium leading-[1.4] text-white">잠 못 주무시고 핸드폰 하시나요?</p>

          <div className="mx-auto mt-4 inline-block bg-[#FCFF68] px-[clamp(12px,2vw,22px)] py-[clamp(6px,1vw,10px)]">
            <p className="text-[clamp(26px,4.6vw,60px)] font-semibold leading-[1.18] text-[#1483FF]">AI가 추천해주는 수면 사운드</p>
          </div>

          <p className="mt-8 text-[clamp(22px,3.6vw,50px)] font-medium leading-[1.35] text-white">
            이 사운드 저 사운드 찾아
            <br />
            떠도는 유목민 생활인 그만!
          </p>

          <div className="relative mx-auto mt-10 w-full max-w-[360px]">
            <div className="aspect-[12/12] w-full">
              <img
                src="/landing/sleep/lp-ai-radar-chart.svg"
                alt="AI 수면 성향 차트"
                className="block h-full w-full object-contain"
              />
            </div>
            <p className="absolute right-[6%] top-[10%] text-[clamp(14px,2.2vw,34px)] font-normal text-white">수면</p>
            <p className="absolute left-[5%] top-[35%] text-[clamp(14px,2.2vw,34px)] font-normal text-white">스트레스</p>
            <p className="absolute left-1/2 top-[87%] -translate-x-1/2 text-[clamp(14px,2.2vw,34px)] font-normal text-white">
              식욕 억제
            </p>
            <span className="absolute right-[18%] top-[20%] h-[clamp(8px,1.6vw,20px)] w-[clamp(8px,1.6vw,20px)] rounded-full bg-[#4F68FC]" />
            <span className="absolute left-[14%] top-[39%] h-[clamp(8px,1.6vw,20px)] w-[clamp(8px,1.6vw,20px)] rounded-full bg-[#29C8A8]" />
            <span className="absolute left-1/2 top-[79%] -translate-x-1/2 h-[clamp(8px,1.6vw,20px)] w-[clamp(8px,1.6vw,20px)] rounded-full bg-[#FBB12C]" />
          </div>

          <p className="mt-14 text-[clamp(30px,4.8vw,60px)] font-medium leading-[1.35] text-white">
            내 건강 상태를 측정하고
            <br />
            맞춤 사운드를 받아요
          </p>

          <div className="relative mx-auto mt-8 flex w-full max-w-[980px] items-center justify-center overflow-hidden px-[clamp(0px,2vw,16px)]">
            <img
              src="/landing/sleep/lp-ai-recommend-card.png"
              alt=""
              aria-hidden
              className="pointer-events-none absolute -left-[9%] top-1/2 w-[clamp(180px,28vw,350px)] -translate-y-1/2 opacity-95"
            />
            <img
              src="/landing/sleep/lp-ai-recommend-card.png"
              alt=""
              aria-hidden
              className="pointer-events-none absolute -right-[9%] top-1/2 w-[clamp(180px,28vw,350px)] -translate-y-1/2 opacity-95"
            />
            <img
              src="/landing/sleep/lp-ai-phone-mockup.png"
              alt="건강 상태 분석 기반 AI 추천 예시 화면"
              className="relative block w-[clamp(250px,47vw,497px)] max-w-full"
            />
          </div>
        </section>

        <section className="bg-[#101010] px-4 pb-16 pt-8 text-center md:px-8">
          <div className="mx-auto w-full max-w-[1040px]">
            <img
              src="/landing/sleep/lp-5078-brain-graphic.png"
              alt="뇌과학 수면 사운드 그래픽"
              className="mx-auto block w-full h-auto max-w-[733px]"
            />

            <p className="mt-8 text-[clamp(22px,4.2vw,50px)] leading-[1.4] tracking-[-0.025em] text-white">
              뇌에 영향을 주는 특정 주파수와
              <br />
              사운드 요소를 결합한
              <br />
              <strong className="font-bold bg-[#2C3779]">‘뇌과학 수면 사운드’</strong>
            </p>

            <p className="mt-10 text-[clamp(22px,4.2vw,50px)] leading-[1.4] tracking-[-0.025em] text-white">
              스트레스 해소, 수면을 위한 안정에
              <br />
              대한 효과가 입증되었어요.
            </p>

            <p className="mt-8 leading-[1.4] tracking-[-0.02em] text-[#6B7684] text-m">
              의료 전문 자문기업 ‘메디허브’의 의료진과 함께한 SR&META 임상시험 진행 결과
            </p>

            <img
              src="/landing/sleep/lp-5078-graphs.png"
              alt="임상시험 전후 비교 그래프"
              className="mx-auto mt-16 block w-full h-auto max-w-[442px]"
            />
          </div>
        </section>

        <section className="bg-[#101010] px-4 pb-24 pt-6 text-center md:px-8">
          <div className="mx-auto w-full max-w-[1040px]">
            <div className="relative mx-auto mt-10 w-full overflow-hidden rounded-[8px] px-4 pb-8 pt-10 md:px-10 md:pt-12">
              <img
                src="/landing/sleep/lp-5078-point1-bg.png"
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-[rgba(3,8,28,0.35)]" />

              <div className="relative">
                <div className="mx-auto mt-10 inline-flex h-[clamp(34px,5vw,62px)] items-center justify-center rounded-full bg-[#4F67F8] px-[clamp(20px,4vw,36px)]">
                  <span className="whitespace-nowrap text-[clamp(22px,3.2vw,42px)] font-semibold leading-none tracking-[-0.02em] text-[#F3F6FF]">
                    POINT 01
                  </span>
                </div>

                <p className="mt-8 text-[clamp(34px,7.8vw,74px)] font-bold leading-[1.15] tracking-[-0.03em] text-white">
                  의료전문가들이
                </p>
                <p className="text-[clamp(34px,7.8vw,74px)] font-bold leading-[1.15] tracking-[-0.03em] text-[#8798FF]">
                  직접 개발에 참여했습니다
                </p>

                <p className="mt-8 text-[clamp(20px,3.8vw,42px)] font-medium leading-[1.35] tracking-[-0.02em] text-[#CFD5E3]">
                  국내 의료 전문가들이
                  <br />
                  사운드필에 직접 참여했어요
                </p>

                <img
                  src="/landing/sleep/lp-5078-point1-laurels.png"
                  alt="보건복지부 승인 5개의 효과, 3건의 논문 출판"
                  className="mx-auto mt-8 block w-full h-auto max-w-[756px]"
                />
              </div>
            </div>

            <div className="mx-auto mt-16 w-full max-w-[879px] space-y-[clamp(18px,3.4vw,32px)]">
              {EXPERT_CARDS.map((expert) => (
                <article
                  key={expert.key}
                  className="w-full rounded-[clamp(14px,2.4vw,30px)] bg-[#F6F6F9] px-[clamp(12px,2.6vw,24px)] py-[clamp(12px,2.6vw,26px)] text-left text-[#303030] shadow-[0_4px_13px_rgba(0,0,0,0.18)]"
                >
                  <div className="flex items-start gap-[clamp(10px,2vw,24px)]">
                    <img
                      src={expert.imageSrc}
                      alt={`${expert.name} 프로필`}
                      className="h-[clamp(74px,19vw,250px)] w-[clamp(74px,19vw,250px)] shrink-0 rounded-full object-cover"
                    />

                    <div className="min-w-0">
                      <p className="text-[clamp(20px,4.4vw,35px)] font-semibold leading-[1.295]">{expert.name}</p>
                      <p className="mt-1 text-[clamp(13px,3.2vw,27px)] font-semibold leading-[1.295] text-[#686868]">
                        {expert.title}
                      </p>

                      <div className="mt-[clamp(8px,1.8vw,14px)] inline-flex rounded-full bg-[#E4EAFF] px-[clamp(10px,2.2vw,23px)] py-[clamp(3px,0.8vw,6px)]">
                        <span className="text-[clamp(12px,2.7vw,25px)] font-semibold leading-[1.295] text-[#3B3B3B]">
                          {expert.badge}
                        </span>
                      </div>

                      <p className="mt-[clamp(10px,2vw,18px)] text-[clamp(13px,2.7vw,30px)] font-medium leading-[1.35] text-[#303030]">
                        {expert.prefix}
                        <span className="font-bold text-[#000AFF]">{expert.highlight}</span>
                        {expert.suffix}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mx-auto mt-20 w-full max-w-[1024px] py-[clamp(42px,10vw,80px)]">
              <div className="mx-auto inline-flex h-[clamp(42px,7.2vw,68px)] min-w-[clamp(170px,44vw,262px)] items-center justify-center rounded-full bg-[#4F67F8] px-[clamp(24px,6vw,44px)]">
                <span className="whitespace-nowrap text-[clamp(20px,5.4vw,49px)] font-semibold leading-none tracking-[-0.02em] text-[#F3F6FF]">
                  POINT 02
                </span>
              </div>
            </div>

            <div className="mt-2 text-center">
              <p className="text-[clamp(34px,7vw,72px)] font-bold leading-[1.2] tracking-[-0.02em] text-[#F1F1F1]">
                이미 테스트로 검증된
              </p>
              <p className="mt-2 text-[clamp(34px,7vw,72px)] font-bold leading-[1.2] tracking-[-0.02em] text-[#8798FF]">
                사운드필의 효과
              </p>
              <p className="mt-6 text-[clamp(20px,3.8vw,40px)] font-medium leading-[1.35] tracking-[-0.02em] text-[#939393]">
                사운드필을 사용한 83% 이상의
                <br />
                사람들이 수면에 도움이 됐습니다!
              </p>
            </div>

            <img
              src="/landing/sleep/lp-5078-validated-people.png"
              alt="사운드필 사용자의 수면 개선 검증 데이터"
              className="mx-auto mt-20 block w-full h-auto max-w-[873px]"
            />

            <div className="mt-16 text-center">
              <p className="text-[clamp(34px,8vw,100px)] font-bold leading-[1.35] text-[#8798FF]">제작기간 3년</p>
              <p className="text-[clamp(34px,8vw,100px)] font-bold leading-[1.35] text-[#28C8A8]">사운드에 진심인</p>
              <p className="mt-6 text-[clamp(24px,5.2vw,64px)] font-medium leading-[1.4] text-white">전문가들이 모여 만든 해결법</p>
            </div>

            <div
              className="mx-auto mt-20 inline-block w-[80%] bg-[#4F67F8] px-[clamp(12px,2vw,20px)] py-[clamp(6px,1vw,11px)] text-white"
              style={{ borderStyle: 'solid', borderColor: '#28C8A8', borderWidth: '0 15px 15px 0px' }}
            >
              <p className="text-[clamp(18px,4.2vw,50px)] font-semibold leading-[1.295] tracking-[-0.02em] ">
                고민은 수면 시간을 늦출 뿐!
              </p>
              <p className="mt-6 text-[clamp(24px,5.2vw,64px)] font-medium leading-[1.4] text-white">지금 시작하세요</p>
              
            </div>

            <button
              type="button"
              onClick={onClickAiCta}
              className="mt-14 mb-6 w-full rounded-full bg-[#4F68FC] px-6 py-[clamp(18px,3.8vw,42px)] text-[clamp(20px,4.2vw,46px)] font-semibold leading-[1.2] tracking-[-0.02em] text-white"
            >
              {CTA_BUTTON_LABEL}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
